import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createProfile } from '../../services/profileService';

const ReviewField = ({ label, value }) => (
    <div className="review-field">
        <span className="review-field-label">{label}</span>
        <span className={`review-field-value ${!value ? 'empty' : ''}`}>
            {value || '—'}
        </span>
    </div>
);

const Step5Review = ({ data, onGoToStep }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [profileName, setProfileName] = useState(data.profile_name || 'My Business Profile');
    const [isDefault, setIsDefault] = useState(data.is_default !== false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const payload = {
                ...data,
                profile_name: profileName.trim() || 'My Business Profile',
                is_default: isDefault,
            };
            const result = await createProfile(payload);
            setSuccess('Business profile saved successfully!');
            localStorage.removeItem('wizardFormData');
            const newProfileId = result?.data?.id;
            // If created from the generator flow, return to generator with the new profile pre-selected.
            // Falls back to /dashboard if the API doesn't return the new profile id.
            if (searchParams.get('returnTo') === 'generator' && newProfileId) {
                setTimeout(() => navigate(`/ai-custom?profileId=${newProfileId}`), 1500);
            } else {
                setTimeout(() => navigate('/dashboard'), 1500);
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to save profile. Please try again.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const address = [data.street_address, data.city, data.state, data.zip_code, data.country]
        .filter(Boolean)
        .join(', ');

    const socialLinks = [
        data.facebook_url,
        data.twitter_url,
        data.instagram_url,
        data.linkedin_url,
        data.youtube_url,
    ].filter(Boolean);

    return (
        <div>
            <h2 className="step-title">Review & Save</h2>
            <p className="step-subtitle">
                Review your business profile before saving. Click "Edit" to make changes.
            </p>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Basic Info */}
            <div className="review-section">
                <div className="review-section-header">
                    <h3 className="review-section-title">Basic Information</h3>
                    <button type="button" className="btn-edit" onClick={() => onGoToStep(1)}>
                        Edit
                    </button>
                </div>
                <ReviewField label="Business Name" value={data.business_name} />
                <ReviewField label="Tagline" value={data.tagline} />
                <ReviewField label="Industry" value={data.industry} />
            </div>

            {/* Contact Info */}
            <div className="review-section">
                <div className="review-section-header">
                    <h3 className="review-section-title">Contact Information</h3>
                    <button type="button" className="btn-edit" onClick={() => onGoToStep(2)}>
                        Edit
                    </button>
                </div>
                <ReviewField label="Email" value={data.email} />
                <ReviewField label="Phone" value={data.phone} />
                <ReviewField label="Address" value={address} />
            </div>

            {/* Services */}
            <div className="review-section">
                <div className="review-section-header">
                    <h3 className="review-section-title">Services</h3>
                    <button type="button" className="btn-edit" onClick={() => onGoToStep(3)}>
                        Edit
                    </button>
                </div>
                {data.services && data.services.length > 0 ? (
                    <div className="review-services-grid">
                        {data.services.map((service, i) => (
                            <div key={i} className="review-service-card">
                                <div className="review-service-icon">{service.icon || '🎯'}</div>
                                <p className="review-service-title">{service.title || '—'}</p>
                                {service.description && (
                                    <p className="review-service-desc">{service.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="review-field-value empty">No services added.</span>
                )}
            </div>

            {/* About & Social */}
            <div className="review-section">
                <div className="review-section-header">
                    <h3 className="review-section-title">About & Social</h3>
                    <button type="button" className="btn-edit" onClick={() => onGoToStep(4)}>
                        Edit
                    </button>
                </div>
                <ReviewField label="About Us" value={data.about_us} />
                <ReviewField label="Mission Statement" value={data.mission_statement} />
                {socialLinks.length > 0 && (
                    <div className="review-field">
                        <span className="review-field-label">Social Links</span>
                        {socialLinks.map((url, i) => (
                            <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="review-field-value"
                                style={{ display: 'block', color: 'var(--primary-color)' }}
                            >
                                {url}
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {/* Save section */}
            <div className="save-section">
                <div className="form-group">
                    <label className="form-label">Profile Name</label>
                    <input
                        type="text"
                        className="form-input"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        maxLength={100}
                        placeholder="My Business Profile"
                    />
                </div>

                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                    />
                    Set as default profile
                </label>

                <div className="save-actions">
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : '💾 Save Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Step5Review;
